// Pure diagnosis rules for the translation queue dashboard: given a queueHealth
// response, name what is wrong. Kept free of React so the tolerances and
// hysteresis are unit-testable.

export type QueueHealthFacts = {
  db: { queued: number; generating: number; stalled: number };
  sqs:
    | { ok: true; visible: number; inFlight: number; delayed: number }
    | { ok: false; message: string };
};

export type QueueDiagnosisKind =
  | 'queueUnreachable'
  | 'stranded'
  | 'ghosts'
  | 'stalled'
  | 'workingBlind';

// Slack before count mismatches count as divergence: SQS attribute counts are
// approximate, and a row and its message are written seconds apart in normal
// operation. Without it, a busy queue flickers false alarms; with only a
// strict-zero rule, a few stranded rows beside a live queue would never show.
export const MISMATCH_TOLERANCE = 3;

export function instantaneousDiagnoses(health: QueueHealthFacts): Set<QueueDiagnosisKind> {
  const kinds = new Set<QueueDiagnosisKind>();
  if (health.db.stalled > 0) {
    kinds.add('stalled');
  }
  if (!health.sqs.ok) {
    kinds.add('queueUnreachable');
    return kinds;
  }
  const waiting = health.sqs.visible + health.sqs.delayed;
  if (health.db.queued > waiting + MISMATCH_TOLERANCE) {
    kinds.add('stranded');
  }
  if (waiting > health.db.queued + MISMATCH_TOLERANCE) {
    kinds.add('ghosts');
  }
  if (health.sqs.inFlight > 0 && health.db.generating === 0) {
    kinds.add('workingBlind');
  }
  return kinds;
}

// Count-based mismatches must persist across several consecutive polls before
// they surface, absorbing SQS's approximate, eventually-consistent counters
// settling after a burst enqueue: a real test enqueueing ~860 rows across
// three courses showed GetQueueAttributes visibly lag the database for a few
// polls. A v1 guess like the stalled threshold below; revisit if it's still
// noisy in practice. Stalled runs and an unreachable queue are not races;
// they show at once.
export const MISMATCH_SUSTAIN_POLLS = 4;

export type DiagnosisStreaks = ReadonlyMap<QueueDiagnosisKind, number>;

export function sustainedDiagnoses(
  streaks: DiagnosisStreaks,
  current: ReadonlySet<QueueDiagnosisKind>,
): { sustained: Set<QueueDiagnosisKind>; streaks: DiagnosisStreaks } {
  const nextStreaks = new Map<QueueDiagnosisKind, number>();
  const sustained = new Set<QueueDiagnosisKind>();
  for (const kind of current) {
    const streak = (streaks.get(kind) ?? 0) + 1;
    nextStreaks.set(kind, streak);
    if (kind === 'stalled' || kind === 'queueUnreachable' || streak >= MISMATCH_SUSTAIN_POLLS) {
      sustained.add(kind);
    }
  }
  return { sustained, streaks: nextStreaks };
}

export type BridgeVerdict =
  | { kind: 'unknown' }
  | { kind: 'match'; dbActive: number; sqsTotal: number }
  | { kind: 'mismatch'; dbActive: number; sqsTotal: number };

// The reconciliation arithmetic shown on the pill between the two system
// panels: everything the DB thinks is active vs everything SQS is holding.
export function bridgeVerdict(health: QueueHealthFacts): BridgeVerdict {
  if (!health.sqs.ok) {
    return { kind: 'unknown' };
  }
  const dbActive = health.db.queued + health.db.generating;
  const sqsTotal = health.sqs.visible + health.sqs.inFlight + health.sqs.delayed;
  return {
    kind: Math.abs(dbActive - sqsTotal) <= MISMATCH_TOLERANCE ? 'match' : 'mismatch',
    dbActive,
    sqsTotal,
  };
}
